import {
    Badge,
    Box,
    Flex,
    Heading,
    IconButton,
    Switch,
    Text,
    Tooltip,
    useToast,
} from '@chakra-ui/react'
import { Icon } from 'components/atoms/Icon'
import { ModalBuild } from 'components/molecules'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { useAuthContext } from 'contexts/auth'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import InstitutionsService from 'services/institutions'
import { CardProps } from './interfaces'

const Card: React.FC<CardProps> = ({
    href,
    title,
    text,
    info,
    header,
    idInstituition,
    active = true
}) => {
    const [currentStatus, setCurrentStatus] = useState<boolean>(active)
    const [loading, setLoading] = useState<boolean>(false)
    const [hideSwitch, setHideSwitch] = useState<boolean>(false)
    const toast = useToast()
    useEffect(() => {
        setCurrentStatus(active)
    }, [active])

    const { isBackoffice } = useAuthContext()

    const deleteModalRef = useRef<ModalBuildHandle>(null)

    const toogleDeleteModal = (open = true): void => {
        if (open) {
            deleteModalRef.current?.onOpen()
            return
        }
        deleteModalRef.current?.onClose()
    }

    const handleDelete = async () => {
        try {
            await InstitutionsService.deleteInstitutionById(idInstituition)
            toast({
                title: 'Instituição removida!',
                description: 'A lista de instituições foi atualizada.',
                position: 'bottom-left',
                status: 'info',
                isClosable: true
            })
            window.location.reload()
        } catch (error: any) {
            const message =
            error?.response?.data[0]?.mensagem ??
            'Algo deu errado, tente novamente mais tarde.'

            toast({
                title: `Erro ao Excluir`,
                description: message,
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            toogleDeleteModal(false)
        }
    }    

    const handleToggleStatus = async (e: any) => {
        try {
            setLoading(true)
            e.stopPropagation()

            await InstitutionsService.toogleStatusInstitutionById(
                idInstituition,
                !currentStatus
            )

            setCurrentStatus(!currentStatus)

            toast({
                title: `Instituição ${
                    currentStatus ? 'inativada' : 'ativada'
                } com sucesso`,
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            setHideSwitch(true)
            toast({
                title: 'Erro ao processar a solicitação',
                description:
                    'Ocorreu um problema inesperado. Por favor, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })

            setTimeout(() => {
                setHideSwitch(false)
            }, 0)
        } finally {
            setLoading(false)
        }
    }

    const navigate = useNavigate()

    const handleNavigation = (e: React.MouseEvent<HTMLDivElement>) => {
        if (
            (e.target as HTMLElement).tagName === 'SPAN' ||
            (e.target as HTMLElement).tagName === 'INPUT'
        )
            return

        if (!currentStatus || loading) return

        navigate(href)
    }
    const cardContent = (
        <Box
            display="flex"
            as="article"
            w="auto"
            h="100%"
            p="16px"
            borderWidth="1px"
            borderColor={currentStatus ? 'grayscale.800' : 'grayscale.300'}
            rounded="md"
            bgColor={currentStatus ? 'white' : 'grayscale.100'}
            shadow={currentStatus ? 'md' : 'sm'}
            justifyContent="space-between"
            flexDirection="column"
            _hover={currentStatus ? { color: 'primary.600' } : undefined}
            opacity={currentStatus ? 1 : 0.7}
            cursor={currentStatus ? 'pointer' : 'default'}
            onClick={handleNavigation}
        >
            {header && (
                <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    justifyContent={'space-between'}
                >
                    <Box
                        fontSize="sm"
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                    >
                        <Badge colorScheme={'primary'} mr="16px">
                            {currentStatus ? 'ATIVO' : 'INATIVO'}
                        </Badge>
                        <Text fontWeight="bold" color={'inherit'}>
                            {header.cnpj}
                        </Text>
                    </Box>
                </Box>
            )}
            <Flex
            alignItems="center"
            justifyContent="space-between"
            pt="16px"
            >
                <Heading
                    size="sm"
                    wordBreak="break-word"
                    color={'inherit'}
                >
                    {title}
                </Heading>

                <Flex alignItems="center" gap="8px">
                    {!hideSwitch && (
                    <Switch
                        defaultChecked={currentStatus}
                        onChange={handleToggleStatus}
                        isDisabled={loading || !isBackoffice}
                    />
                    )}

                    {isBackoffice && (
                    <IconButton
                        aria-label="Excluir instituição"
                        icon={<Icon name="delet" />}
                        size="sm"
                        onClick={e => {
                        e.stopPropagation()
                        toogleDeleteModal()
                        }}
                    />
                    )}
                </Flex>
            </Flex>    
            {text && <Text color={'inherit'}>{text}</Text>}
            {info && (
                <Flex color={'inherit'}>
                    {info.map((i, k) => (
                        <Box key={k} display="flex" alignItems="center">
                            <Icon name={i.icon} />
                            {i.label}
                        </Box>
                    ))}
                </Flex>
            )}
        </Box>
    )

    if (!currentStatus) {
        return (
            <Tooltip
                label="Esta Instituição Financeira está temporariamente inativa e não pode ser acessada."
                placement="top"
            >
                {cardContent}
            </Tooltip>
        )
    }

    return (
    <>
        {cardContent}

        <ModalBuild
        ref={deleteModalRef}
        closeOnOverlayClick={false}
        title="Confirmação de exclusão"
        size="md"
        actions={{
            position: 'flex-end',
            childrens: [
            {
                text: 'Cancelar',
                handle: 'cancel',
                variant: 'outline'
            },
            {
                text: 'Deletar',
                handle: 'confirm',
                colorScheme: 'red'
            }
            ]
        }}
        onConfirm={handleDelete}
        onCancel={() => toogleDeleteModal(false)}
        >
        <Text px={6} py={4}>
            Você realmente deseja deletar esta instituição?
            Uma vez deletada, não será possível voltar atrás.
        </Text>
        </ModalBuild>
    </>
    )

}

export default Card
